import { Container, Button } from 'react-bootstrap'
import { FaqSection, HeroSection, SectionWithImage, TestimonialSection, LabSection, PricingSection, ExploreTextbookSection, ExploreVideoLessonSection } from './Components/SectionTemplates'
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import { faArrowDown } from "@fortawesome/free-solid-svg-icons"

const faqs = [
    {
        question: "What is STEM Sims and how does it work?",
        answer: "STEM Sims is a comprehensive science curriculum designed for homeschoolers. Our Grades 3-8 comprehensive curriculum also includes over 350 interactive virtual labs and 200 hands-on experiments, allowing students to engage with advanced scientific concepts from home. Course-specific labs for high school science subjects, including AP, are part of the package. The platform is accessible online, with step-by-step guidance through each experiment and simulation."
    },
    {
        question: "What grade levels can use STEM Sims?",
        answer: "STEM Sims is primarily for students in Grades 3-5, 6-8, and 9-12 +AP. Introductory science explorations for Grades K-2 students are a fun starting point for youngest learners. Our wide range of experiments and simulations can be adapted for various skill levels, making it flexible for the different ages and abilities within your homeschool setting."
    },
    {
        question: "What equipment do I need to use STEM Sims?",
        answer: "For the virtual labs, you only need a computer or tablet with an internet connection. For the 200 hands-on experiments, most materials are common household items. We provide a list of required materials for each experiment, making it so easy to prepare. For the curriculum options, just print out what you need."
    },
    {
        question: "How much does STEM Sims cost?",
        answer: "STEM Sims offers a homeschool subscription plan starting at only $150/year. This provides full access to all 350+ virtual labs and 200 hands-on experiments, making it a very cost-effective alternative to expensive physical lab kits. Add curriculum for Grades 3-5 or Grades 6-8 for an additional $150/year."
    },
    {
        question: "I'm concerned about too much screen time. How does STEM Sims address this?",
        answer: "While STEM Sims does include virtual labs, we've balanced this with 200 hands-on experiments. Additionally, the interactive nature of our virtual labs encourages active engagement rather than passive screen time. Many families find that the time saved on setup and cleanup allows for more meaningful science exploration. Optional curriculum books can be printed and completed on paper."
    },
    {
        question: "How effective are virtual labs for science education?",
        answer: "Virtual learning through STEM Sims is highly effective. Our simulations let students interact with common science experiments and even advanced scientific tools not typically available in homes or schools. Combined with the ability to repeat experiments and the self-paced nature of the platform, our platform can accelerate learning. Students show improved understanding and retention of scientific concepts."
    },
    {
        question: "I'm not a science expert. Will I be able to guide my homeschooler through STEM Sims effectively?",
        answer: "Absolutely! STEM Sims is designed with homeschool parents in mind. Our platform provides clear, step-by-step instruction plus video lessons for each lab and experiment. Plus, our curriculum is developed by experienced science educators and homeschool parents, ensuring it's both comprehensive and user-friendly."
    },
    {
        question: "What about alignment with educational standards?",
        answer: "Our expert-designed curriculum ensures that students can meet or exceed grade-level expectations, preparing them for standardized tests and future academic challenges."
    }
]

const testimonials = [
    {
        name: "Homeschool tutor, Florida",
        quote: "STEM Sims is an amazing resource for me and for my students in biology (middle grades), anatomy and physiology (high school), and chemistry (high school)."
    },
    {
        name: "Homeschool parent, Gainesville, Florida",
        quote: "This is a wonderful program, and I will be sharing it with my friends!"
    },
    {
        name: "Science teacher, North Palm Beach, Florida",
        quote: "We did the Trench Dive simulation, and I have to tell you how AMAZING it was! The kids enjoyed it! They were ALL engaged the entire block period (90 minutes). We did all 5 lessons. We talked and discussed water, sunlight, the different ocean levels, and so much more. I REALLY liked the background information. I can't wait to do the next one!"
    },
    {
        name: "7th Grade Integrated Science Teacher, Orlando, Florida",
        quote: "I can see that these modules could spark the interest even in those students who dare us to make them interested in science."
    },
]

const plans = [
    {
        planName: "Family",
        price: "$100",
        description: "Includes up to 4 students.",
        features: [
            "350+ Interactive Virtual Labs",
            "200 Hands-On Labs",
            "2,000+ Grade-Level Lessons",
            "10,000+ Assessment Items",
            "How-to Guides for Labs",
            "Teacher Guides",
            "Class Management Tools"
        ],
        studentCount: 4,
        buttonText: "Get Started - Family"
    },
    {
        planName: "Family Plus 3-5",
        price: "$200",
        description: "Teach your little ones with confidence - our Grades 3-5 curriculum is designed just for them.",
        features: [
            "Everything in Family",
            "Adds tailored curriculum for Grades 3-5"
        ],
        studentCount: 4,
        buttonText: "Get Started - Family Plus 3-5"
    },
    {
        planName: "Family Plus 6-8",
        price: "$200",
        description: "Unlock our Grades 6-8 curriculum, designed to engage and educate your older students.",
        features: [
            "Everything in Family",
            "Adds tailored curriculum for Grades 6-8"
        ],
        studentCount: 4,
        buttonText: "Get Started - Family Plus 6-8"
    },
    {
        planName: "Family Plus",
        price: "$300",
        description: "Unlock the ultimate homeschool experience for your entire family! Get everything in Family, plus our comprehensive Grades 3-5 and 6-8 curriculum, tailored to help your students thrive.",
        features: [
            "Everything in Family",
            "All our curriculum for Grades 3-5 and 6-8"
        ],
        studentCount: 4,
        buttonText: "Get Started - Family Plus"
    }
]

const handleScroll = () => {
    window.scrollBy({
        top: window.innerHeight - 100, // Scroll by one page height
        left: 0,
        behavior: "smooth"
    })
}

export default function Homeschool() {
    return (
        <div style={{ backgroundImage: 'url(/images/landing/hex-back.jpg)', backgroundPosition: 'center' }}>
            <Container fluid="md" className="bg-white shadow px-0">
                <HeroSection title="Everything You Need for Homeschool Science!" subtitle="Ignite Curiosity and Delight Homeschoolers with Exciting Curriculum, Hands-On Projects, and Interactive Virtual Labs." image="/images/landing/homeschool/homeschool-child.png" />
                <SectionWithImage title="Your Affordable Science Teaching Assistant" sectionContents={WhoSection()} image="/images/landing/homeschool/trio.png" imagePosition="left" />
                <Button onClick={handleScroll} 
                    className="btn btn-primary rounded-circle d-flex justify-content-center align-items-center fixed-bottom m-auto"
                    style={{height: "50px", width: "50px", bottom: "20px", opacity: "60%"}}>
                    <FontAwesomeIcon icon={faArrowDown} size="2x" style={{ animation:"fa-fade 5s infinite ease-in" }} />
                </Button>
                <TestimonialSection testimonials={testimonials} />
                <SectionWithImage title="Bring Science Into Your Home" sectionContents={HowSection()} image="/images/landing/homeschool/duo2.png" imagePosition="right" />
                <ExploreVideoLessonSection />
                <ExploreTextbookSection />
                <LabSection />
                <SectionWithImage title="Backed By Research & Academic Experience" sectionContents={AuthoritySection()} image="/images/landing/homeschool/kidFishEx.png" imagePosition="left" backgroundColor='rgba(114, 124, 200, 0.25)'/>
                <SectionWithImage title="Pathway to Success" sectionContents={VisionSection()} image="/images/landing/homeschool/kid10.png" imagePosition="right" />
                {/* <Section title="Pricing" sectionContents={PricingSection()} backgroundColor='rgba(114, 124, 200, 0.25)'/> */}
                <PricingSection plans={plans} backgroundColor='rgba(114, 124, 200, 0.25)'/>
                <FaqSection faqs={faqs} />
            </Container>
        </div>
    )
}

function WhoSection() {
    return (
        <div className="text-start">
            <p>From complex curriculum to lack of lab equipment, we understand the unique challenges homeschoolers face. That’s why our mission is to make science learning fun, interactive, and stress-free for you and your homeschoolers.</p>

            <p><b>Teaching complex science concepts at home and want the best science experience for your students?</b></p>
            <p>Our hand-on labs use common household materials and our virtual labs teach the same concepts found in the best-equipped science learning environments. Printable books for Grades 3-5 and Grades 6-8 homeschoolers provide guided curriculum developed by expert educators. Assessments identify areas of strengths and weaknesses and help prepare for end-of-year tests.</p>
            <p>With STEM Sims, your homeschoolers learn science within the same environment, from kindergarten through high school, from basic concepts through advanced chemistry, biology, physics, AP, and more.</p>
            
            <p><b>Our pricing is unbeatable, starting at just $150/year for up to 4 students!</b></p>

            <div className="d-flex justify-content-between align-items-center">
                <Button variant="primary" className="mt-4 d-block mb-auto" onClick={() => document.getElementById('pricing').scrollIntoView({ behavior: 'smooth' })}>Sign Up For Success</Button>
                <div className="d-flex-column justify-content-center text-center w-25 bg-light p-2 mt-4 rounded">
                    <a href="https://www.stepupforstudents.org/scholarships/myscholarshop/" className="text-decoration-none text-dark" target="_blank" rel="noopener noreferrer" onClick={(e) => {e.preventDefault(); window.open('https://www.stepupforstudents.org/scholarships/myscholarshop/', 'MyScholarShop');}}>
                        <img src="/images/landing/homeschool/mss-logo.png" alt="MyScholarShop Logo" className="w-100" />
                        Also available in MyScholarShop
                    </a>
                </div>
            </div>

            
        </div>
    )
}

function HowSection() {
    return (
        <div className="text-start">
            <ul>
                <li><strong>Achieve Science Mastery:</strong> Empower your homeschooler with high-quality science education.</li>
                <li><strong>Independent, Self-Paced Learning for Grades 3-12 & AP:</strong> Nurture curiosity and build confidence as your students explore basic to advanced science concepts.</li>
                <li><strong>Mess-Free, No-Prep Virtual Labs:</strong> More learning, less stress.</li>
                <li><strong>A Wide Range of Experiments:</strong> From Plant Growth to Drones to Wind Tunnels and Mass Spectrometers.</li>
                <li><strong>Expert-Designed:</strong> Trustworthy science education.</li>
            </ul>
            <p>Our secure and effortless integrations allow you to seamlessly incorporate STEM Sims into your learning environment.</p>
        </div>
    )
}

function AuthoritySection() {
    return (
        <div className="text-start">
            <p>Our team of veteran educators and homeschooling parents has over 70 years of combined STEM (Science, Technology, Engineering, and Math) experience. Backed by research conducted over the past decade, we've leveraged cutting-edge technology to create STEM Sims. Studies show significant learning gains on standardized testing with STEM Sims.</p>
        </div>
    )
}

function VisionSection() {
    return (
        <div className="text-start">
            <p>With STEM Sims, teaching in-depth science concepts can be turned into an interactive adventure. Fuel your student's curiosity and academic success, while eliminating the stress, cost, and mess of dealing with so many science lab kits.</p>
            <ul>
                <li>Develop strong critical thinking and scientific skills.</li>
                <li>Foster your student's love for science beyond the curriculum.</li>
                <li>Empower yourself as a confident science educator.</li>
                <li>Enjoy a flexible and efficient homeschool schedule.</li>
                <li>Prepare your homeschooler for future STEM opportunities.</li>
            </ul>

            <Button variant="primary" className="mt-4 d-block" onClick={() => document.getElementById('pricing').scrollIntoView({ behavior: 'smooth' })}>Let's Go!</Button>
        </div>
    )
}

